// Design-Tokens für das Admin-Dashboard. Gespiegelt aus
// curalearn-app/src/lib/theme.ts (Source of Truth) — Werte müssen
// identisch bleiben, damit App und Admin visuell konsistent sind.
//
// Wenn ein Token in der App geändert wird: hier nachziehen.
// Vice versa: nicht hier divergieren ohne Abstimmung mit der App.

export interface ThemeColors {
  // Hintergründe (von hinten nach vorne)
  bgBase: string;
  bgSurface: string;
  bgElevated: string;
  bgOverlay: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;

  // Akzent
  accent: string;
  accentLight: string;
  accentSubtle: string;

  // Status
  success: string;
  successBg: string;
  warning: string;
  warningBg: string;
  danger: string;
  dangerBg: string;
  info: string;
  infoBg: string;

  // Rahmen
  border: string;
  borderSubtle: string;
  borderStrong: string;

  // Themen-Farben (für Topic-Badges, Charts)
  topicAnatomy: string;
  topicPhysiology: string;
  topicPathology: string;
  topicPharma: string;
  topicLaw: string;
  topicDiagnostics: string;
}

export interface ThemeSpacing {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  '2xl': number;
  '3xl': number;
}

export interface ThemeRadii {
  sm: number;
  md: number;
  lg: number;
  xl: number;
  full: number;
}

export interface Theme {
  mode: 'dark' | 'light';
  colors: ThemeColors;
  spacing: ThemeSpacing;
  radii: ThemeRadii;
}

const ACCENT = '#7C3AED';

const sharedTopics = {
  topicAnatomy: '#60A5FA',
  topicPhysiology: '#34D399',
  topicPathology: '#F87171',
  topicPharma: '#A78BFA',
  topicLaw: '#FBBF24',
  topicDiagnostics: '#FB923C',
};

const sharedSpacing: ThemeSpacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 28,
  '2xl': 36,
  '3xl': 56,
};

const sharedRadii: ThemeRadii = {
  sm: 10,
  md: 14,
  lg: 20,
  xl: 28,
  full: 9999,
};

export const darkTheme: Theme = {
  mode: 'dark',
  colors: {
    bgBase: '#0F0F1E',
    bgSurface: '#1A1A2E',
    bgElevated: '#222240',
    bgOverlay: '#2A2A4A',

    textPrimary: '#E8E8F0',
    textSecondary: '#9898B8',
    textTertiary: '#686888',

    accent: ACCENT,
    accentLight: '#A78BFA',
    accentSubtle: '#2D2D50',

    success: '#34D399',
    successBg: '#0D3B2E',
    warning: '#FBBF24',
    warningBg: '#3B3518',
    danger: '#F87171',
    dangerBg: '#3B1818',
    info: '#60A5FA',
    infoBg: '#1A2A4A',

    border: '#333355',
    borderSubtle: '#2D2D44',
    borderStrong: '#4D4D6C',

    ...sharedTopics,
  },
  spacing: sharedSpacing,
  radii: sharedRadii,
};

export const lightTheme: Theme = {
  mode: 'light',
  colors: {
    bgBase: '#F2F2F7',
    bgSurface: '#FFFFFF',
    bgElevated: '#E5E5EA',
    bgOverlay: '#FFFFFF',

    textPrimary: '#1A1A2E',
    textSecondary: '#5A5A7A',
    textTertiary: '#9999B0',

    accent: ACCENT,
    accentLight: '#6D28D9',
    accentSubtle: '#EDE9FE',

    success: '#34D399',
    successBg: '#ECFDF5',
    warning: '#FBBF24',
    warningBg: '#FFFBEB',
    danger: '#F87171',
    dangerBg: '#FEF2F2',
    info: '#60A5FA',
    infoBg: '#EFF6FF',

    border: '#D0CEE0',
    borderSubtle: '#E4E2EE',
    borderStrong: '#B8B6CC',

    ...sharedTopics,
  },
  spacing: sharedSpacing,
  radii: sharedRadii,
};

export const FONT_FAMILY = "'Inter', system-ui, -apple-system, sans-serif";
export const FONT_FAMILY_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
